import { useState } from 'react';
import { Loader2, RefreshCw, Check, X, Send, Settings, ChevronDown, ChevronUp, Eye, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAIMatches, useRunAIMatching, useUpdateMatchStatus } from '@/hooks/useAIMatching';
import { useAISettings, useUpdateAISetting } from '@/hooks/useAISettings';
import { useIntroductions } from '@/hooks/useIntroductions';
import { useEvents } from '@/hooks/useEvents';
import { estimateMatchingCost, formatCents } from '@/lib/ai-cost';
import { IntroductionDialog } from './AdminMatching.IntroductionDialog';
import { MatchingCriteriaTab } from './AdminAISettings';
import type { Match, MatchStatus } from '@/types/ai';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Legacy matchups tab — import existing component
import AdminMatchups from './AdminMatchups';

const STATUS_COLORS: Record<MatchStatus, string> = {
  suggested: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  introduced: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  connected: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  declined: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export default function AdminMatching() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Matching</h1>
        <p className="text-muted-foreground text-sm">
          Manage weekly matchups and AI-powered member matching
        </p>
      </div>

      <Tabs defaultValue="ai">
        <TabsList>
          <TabsTrigger value="ai">AI Matching</TabsTrigger>
          <TabsTrigger value="weekly">Weekly Matchups</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-6">
          <AIMatchingTab />
        </TabsContent>

        <TabsContent value="weekly" className="mt-6">
          <AdminMatchups />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AIMatchingTab() {
  const { data: settings } = useAISettings();
  const updateSetting = useUpdateAISetting();
  const runMatching = useRunAIMatching();
  const updateStatus = useUpdateMatchStatus();
  const { toast } = useToast();

  // State
  const [cohortFilter, setCohortFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [batchFilter, setBatchFilter] = useState<string>('all');
  const [eventFilter, setEventFilter] = useState<string>('none');
  const [introMatchId, setIntroMatchId] = useState<string | null>(null);
  const [viewIntroMatchId, setViewIntroMatchId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [maxMatchesOverride, setMaxMatchesOverride] = useState<number | ''>('');
  const [expandedReasons, setExpandedReasons] = useState<Set<string>>(new Set());

  // Fetch matches with status filter
  const filterParams = statusFilter !== 'all' ? { status: statusFilter as MatchStatus } : undefined;
  const { data: matchData, isLoading } = useAIMatches(filterParams);

  // Fetch cohorts for filter
  const { data: cohorts } = useQuery({
    queryKey: ['cohorts-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('cohorts')
        .select('name')
        .order('name');
      return data?.map((c) => c.name) || [];
    },
  });

  // Fetch events for event-based matching
  const { data: events } = useEvents();

  // Get selected event details
  const selectedEvent = eventFilter !== 'none' && events
    ? events.find((e) => e.id === eventFilter)
    : null;

  // Effective member count: event attendees or cohort-filtered members
  const { data: memberCount } = useQuery({
    queryKey: ['approved-member-count', cohortFilter, eventFilter],
    queryFn: async () => {
      if (selectedEvent) {
        return selectedEvent.attendees.length;
      }
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('onboarding_completed', true)
        .eq('membership_status', 'approved');

      if (cohortFilter && cohortFilter !== 'all') {
        query = query.eq('city_cohort', cohortFilter);
      }

      const { count } = await query;
      return count || 0;
    },
  });

  const model = settings?.ai?.features?.matching?.model || 'claude-haiku-4-5-20251001';
  const estimate = memberCount ? estimateMatchingCost(memberCount, model) : null;

  const handleGenerateClick = () => {
    setCustomInstructions('');
    setMaxMatchesOverride('');
    setPromptOpen(true);
  };

  const handlePromptContinue = () => {
    setPromptOpen(false);
    setConfirmOpen(true);
  };

  const handleRunMatching = async () => {
    setConfirmOpen(false);
    try {
      const params: { cohort_filter?: string; member_ids?: string[]; custom_instructions?: string; max_matches_override?: number } = {};
      if (cohortFilter && cohortFilter !== 'all') {
        params.cohort_filter = cohortFilter;
      }
      // Event-based: pass attendee user_ids
      if (selectedEvent) {
        params.member_ids = selectedEvent.attendees.map((a) => a.user_id);
      }
      if (customInstructions.trim()) {
        params.custom_instructions = customInstructions.trim();
      }
      if (maxMatchesOverride !== '' && maxMatchesOverride > 0) {
        params.max_matches_override = maxMatchesOverride;
      }

      const result = await runMatching.mutateAsync(params);
      toast({
        title: 'Matches generated',
        description: `${result.matches} matches from ${result.members} members`,
      });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleStatusUpdate = async (matchId: string, status: MatchStatus) => {
    try {
      await updateStatus.mutateAsync({ matchId, status });
      toast({ title: `Match ${status}` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const toggleReason = (matchId: string) => {
    setExpandedReasons((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const matches = matchData?.matches || [];
  const profiles = matchData?.profiles || {};

  // Group by batch
  const batches = new Map<string, Match[]>();
  for (const match of matches) {
    const key = match.batch_id || 'ungrouped';
    if (!batches.has(key)) batches.set(key, []);
    batches.get(key)!.push(match);
  }

  // Get unique batch IDs for batch filter
  const batchIds = Array.from(batches.keys());

  // Filter by batch
  const filteredBatches = batchFilter === 'all'
    ? Array.from(batches.entries())
    : Array.from(batches.entries()).filter(([id]) => id === batchFilter);

  return (
    <div className="space-y-6">
      {/* Run Matching Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Generate Matches</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setSettingsOpen(true)}
              title="Matching criteria settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-2">
              <label className="text-sm font-medium">Cohort Filter</label>
              <Select value={cohortFilter} onValueChange={setCohortFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All cohorts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cohorts</SelectItem>
                  {(cohorts || []).map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                <Calendar className="h-3 w-3 inline mr-1" />
                Event
              </label>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue placeholder="No event filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No event filter</SelectItem>
                  {(events || []).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.title} ({e.attendees.length} attendees)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="text-sm text-muted-foreground">
              {memberCount !== undefined && (
                <span>
                  {memberCount} eligible member{memberCount !== 1 ? 's' : ''}
                  {selectedEvent ? ' (event attendees)' : ''}
                </span>
              )}
              {estimate && (
                <span className="ml-2">
                  (est. {formatCents(estimate.estimatedCostCents)})
                </span>
              )}
            </div>

            <Button
              disabled={runMatching.isPending || !memberCount || memberCount < 2}
              onClick={handleGenerateClick}
            >
              {runMatching.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Generate Matches
            </Button>
          </div>

          {/* Criteria preview */}
          {settings?.matchCriteria && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Current criteria: </span>
              Mutual Benefit {settings.matchCriteria.mutual_benefit_weight}% ·
              Skills {settings.matchCriteria.skills_weight}% ·
              Industry {settings.matchCriteria.industry_weight}% ·
              Experience {settings.matchCriteria.experience_level_weight}% ·
              Location {settings.matchCriteria.location_weight}% ·
              Min score {settings.matchCriteria.min_score_threshold} ·
              Max {settings.matchCriteria.max_matches_per_member}/member
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters for match results */}
      {matches.length > 0 && (
        <div className="flex items-center gap-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="suggested">Suggested</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="introduced">Introduced</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Batch</label>
            <Select value={batchFilter} onValueChange={setBatchFilter}>
              <SelectTrigger className="w-[200px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All batches</SelectItem>
                {batchIds.map((id) => {
                  const first = batches.get(id)?.[0];
                  const date = first ? new Date(first.created_at).toLocaleDateString() : '';
                  return (
                    <SelectItem key={id} value={id}>
                      {id === 'ungrouped' ? 'Ungrouped' : `${id.slice(0, 8)} — ${date}`}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Match Results */}
      {matches.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">No AI matches yet. Click "Generate Matches" to create the first batch.</p>
          </CardContent>
        </Card>
      ) : (
        filteredBatches.map(([batchId, batchMatches]) => (
          <Card key={batchId}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {batchId === 'ungrouped' ? 'Matches' : `Batch ${batchId.slice(0, 8)}`}
                </CardTitle>
                <span className="text-sm text-muted-foreground">
                  {batchMatches.length} matches · {new Date(batchMatches[0].created_at).toLocaleDateString()}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member 1</TableHead>
                    <TableHead>Member 2</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batchMatches.map((match) => {
                    const p1 = profiles[match.user_id_1];
                    const p2 = profiles[match.user_id_2];
                    const isExpanded = expandedReasons.has(match.id);

                    return (
                      <TableRow key={match.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <SignedAvatarImage src={p1?.avatar_url || undefined} />
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {p1?.full_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{p1?.full_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[p1?.role, p1?.company].filter(Boolean).join(' at ')}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <SignedAvatarImage src={p2?.avatar_url || undefined} />
                              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                {p2?.full_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{p2?.full_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[p2?.role, p2?.company].filter(Boolean).join(' at ')}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-mono text-sm">{match.score ?? '—'}</span>
                        </TableCell>
                        <TableCell>
                          {match.ai_reason ? (
                            <Collapsible open={isExpanded} onOpenChange={() => toggleReason(match.id)}>
                              <div className="flex items-start gap-1 max-w-[300px]">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 mt-0.5 shrink-0">
                                    {isExpanded ? (
                                      <ChevronUp className="h-3 w-3" />
                                    ) : (
                                      <ChevronDown className="h-3 w-3" />
                                    )}
                                  </Button>
                                </CollapsibleTrigger>
                                {!isExpanded && (
                                  <p className="text-sm text-muted-foreground truncate">
                                    {match.ai_reason}
                                  </p>
                                )}
                              </div>
                              <CollapsibleContent>
                                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                                  {match.ai_reason}
                                </p>
                              </CollapsibleContent>
                            </Collapsible>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[match.status]}`}>
                            {match.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {match.status === 'suggested' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleStatusUpdate(match.id, 'approved')}
                                  title="Approve"
                                >
                                  <Check className="h-4 w-4 text-green-600" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleStatusUpdate(match.id, 'declined')}
                                  title="Decline"
                                >
                                  <X className="h-4 w-4 text-red-600" />
                                </Button>
                              </>
                            )}
                            {match.status === 'approved' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setIntroMatchId(match.id)}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Introduce
                              </Button>
                            )}
                            {match.status === 'introduced' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8"
                                onClick={() => setViewIntroMatchId(match.id)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View Intro
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      {/* Settings Sheet */}
      <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Matching Criteria</SheetTitle>
          </SheetHeader>
          <div className="mt-6">
            {settings?.matchCriteria && (
              <MatchingCriteriaTab
                criteria={settings.matchCriteria}
                onUpdate={(updated) => {
                  updateSetting.mutate(
                    { key: 'match_criteria', value: updated },
                    {
                      onSuccess: () => {
                        toast({ title: 'Matching criteria saved' });
                        setSettingsOpen(false);
                      },
                    }
                  );
                }}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Pre-generation Prompt Dialog */}
      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure This Run</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Custom instructions for this run</Label>
              <Textarea
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="Any specific instructions? (e.g., 'Focus on cross-industry matches', 'Prioritize new members')"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">One-time only — not saved to settings.</p>
            </div>
            <div className="space-y-2">
              <Label>Max matches per member (override)</Label>
              <Input
                type="number"
                value={maxMatchesOverride}
                onChange={(e) => setMaxMatchesOverride(e.target.value ? parseInt(e.target.value) : '')}
                placeholder={`Default: ${settings?.matchCriteria?.max_matches_per_member ?? 5}`}
                min={1}
                max={20}
                className="w-40"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromptOpen(false)}>Cancel</Button>
            <Button onClick={handlePromptContinue}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost Estimate Confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate AI Matches</AlertDialogTitle>
            <AlertDialogDescription>
              This will analyze {memberCount} member{memberCount !== 1 ? 's' : ''}
              {cohortFilter !== 'all' ? ` in ${cohortFilter}` : ''}
              {selectedEvent ? ` attending "${selectedEvent.title}"` : ''}
              {' '}using {model}.
              {estimate && (
                <>
                  <br /><br />
                  Estimated cost: {formatCents(estimate.estimatedCostCents)}
                  <br />
                  Estimated tokens: ~{estimate.estimatedInputTokens.toLocaleString()} input / ~{estimate.estimatedOutputTokens.toLocaleString()} output
                </>
              )}
              {customInstructions.trim() && (
                <>
                  <br /><br />
                  Custom instructions: "{customInstructions.trim().slice(0, 100)}{customInstructions.trim().length > 100 ? '...' : ''}"
                </>
              )}
              {maxMatchesOverride !== '' && (
                <>
                  <br />
                  Max matches override: {maxMatchesOverride}/member
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRunMatching}>Generate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Introduction Dialog */}
      {introMatchId && (
        <IntroductionDialog
          matchId={introMatchId}
          open={!!introMatchId}
          onClose={() => setIntroMatchId(null)}
        />
      )}

      {/* View Introduction Dialog */}
      {viewIntroMatchId && (
        <ViewIntroductionDialog
          matchId={viewIntroMatchId}
          open={!!viewIntroMatchId}
          onClose={() => setViewIntroMatchId(null)}
        />
      )}
    </div>
  );
}

function ViewIntroductionDialog({
  matchId,
  open,
  onClose,
}: {
  matchId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data: introductions, isLoading } = useIntroductions(matchId);
  const intro = introductions?.[0];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Introduction</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : intro ? (
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs text-muted-foreground">Subject</Label>
              <p className="text-sm font-medium mt-1">{intro.subject}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Body</Label>
              <div className="mt-1 p-3 bg-muted/50 rounded-lg text-sm whitespace-pre-wrap">
                {intro.body}
              </div>
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Sent via: {intro.sent_via}</span>
              <span>Created: {new Date(intro.created_at).toLocaleString()}</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">No introduction found for this match.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
